import { useState, useCallback } from "react";
import { apiRequest } from "../context/AuthContext";
import {
  GraduationCap, Plus, Trash2, Copy, Check, Loader2, Save,
  ChevronDown, TrendingUp, Info, AlertTriangle
} from "lucide-react";

// ── Static grade data (mirrors backend tables) ────────────────────────────────

// IB Diploma: 24-45 point scale
function _ibScoreToGpa(score) {
  if (score >= 40) return 4.0;
  if (score >= 37) return 3.7;
  if (score >= 34) return 3.3;
  if (score >= 30) return 3.0;
  if (score >= 27) return 2.7;
  if (score >= 24) return 2.3;
  if (score >= 20) return 2.0;
  return 1.5;
}

const IB_SCORES = Array.from({ length: 22 }, (_, i) => String(45 - i)); // "45" → "24"

const GRADE_OPTIONS = {
  alevel:           ["A*", "A", "B", "C", "D", "E"],
  btec_diploma:     ["D*", "D", "M", "P"],
  btec_certificate: ["D*", "D", "M", "P"],
  scottish_higher:  ["A", "B", "C", "D"],
  gcse:             ["9", "8", "7", "6", "5", "4", "3", "2", "1"],
  ib_diploma:       IB_SCORES,
};

const GRADE_GPA = {
  alevel:           { "A*": 4.0, A: 4.0, B: 3.3, C: 2.7, D: 2.0, E: 1.0 },
  btec_diploma:     { "D*": 4.0, D: 3.7, M: 3.0, P: 2.0 },
  btec_certificate: { "D*": 4.0, D: 3.7, M: 3.0, P: 2.0 },
  scottish_higher:  { A: 4.0, B: 3.3, C: 2.7, D: 2.0 },
  ib_diploma:       Object.fromEntries(IB_SCORES.map(s => [s, _ibScoreToGpa(Number(s))])),
};

const WEIGHTS = {
  alevel: 1, btec_diploma: 2, btec_certificate: 1, scottish_higher: 1,
  ib_diploma: 3, gcse: 0,
};

const TYPE_LABELS = {
  alevel: "A-Level",
  btec_diploma: "BTEC Diploma",
  btec_certificate: "BTEC Certificate",
  scottish_higher: "Scottish Higher",
  ib_diploma: "IB Diploma",
  gcse: "GCSE (excluded)",
};

const SCOREABLE_TYPES = ["alevel", "btec_diploma", "btec_certificate", "scottish_higher", "ib_diploma"];

// D1 eligible ≥ 2.5 (borderline ≥ 2.3), D2 eligible ≥ 2.4 (borderline ≥ 2.2)
// D3 / NAIA eligible ≥ 2.0 (no borderline)
const DIVISIONS = [
  { key: "d1_status",   label: "NCAA D1", threshold: 2.5 },
  { key: "d2_status",   label: "NCAA D2", threshold: 2.4 },
  { key: "d3_status",   label: "NCAA D3", threshold: 2.0 },
  { key: "naia_status", label: "NAIA",    threshold: 2.0 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusColors(status) {
  if (status === "Eligible")    return { bg: "bg-green-50",  border: "border-green-200", text: "text-green-700",  badge: "bg-green-500"  };
  if (status === "Borderline")  return { bg: "bg-amber-50",  border: "border-amber-200", text: "text-amber-700",  badge: "bg-amber-500"  };
  return                               { bg: "bg-red-50",    border: "border-red-200",   text: "text-red-700",    badge: "bg-red-500"    };
}

function statusIcon(status) {
  if (status === "Eligible")   return <Check className="w-4 h-4" />;
  if (status === "Borderline") return <AlertTriangle className="w-4 h-4" />;
  return <span className="text-xs font-black">✕</span>;
}

/**
 * Given current scoring quals (sum_points, total_weight) and a target GPA threshold,
 * find the minimum grade in each qualification type that reaches target.
 * Returns an object keyed by type with the lowest acceptable grade string (or null/message).
 */
function computeReverseLookup(sumPoints, totalWeight, targetGpa) {
  const result = {};
  for (const qType of SCOREABLE_TYPES) {
    const w = WEIGHTS[qType];
    const needed = (targetGpa * (totalWeight + w) - sumPoints) / w;

    if (needed <= 0) {
      result[qType] = "Any grade";
      continue;
    }
    if (needed > 4.0) {
      result[qType] = null; // Not achievable
      continue;
    }

    // Find the lowest grade (worst) that still satisfies needed GPA
    const table = GRADE_GPA[qType];
    const grades = GRADE_OPTIONS[qType]; // ordered best→worst
    let minGrade = null;
    for (const g of grades) {
      if (table[g] >= needed) minGrade = g;
    }
    result[qType] = minGrade;
  }
  return result;
}

// ── Component ─────────────────────────────────────────────────────────────────

const EMPTY_ROW = () => ({ id: Date.now() + Math.random(), type: "alevel", grade: "A" });

export default function GpaCalculatorPage() {
  const [rows, setRows]         = useState([EMPTY_ROW()]);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [copied, setCopied]     = useState(false);
  const [savedGpa, setSavedGpa] = useState(false);   // true after a successful save
  const [savingGpa, setSavingGpa] = useState(false); // in-flight state
  const [saveError, setSaveError] = useState("");

  // ── Row management ──────────────────────────────────────────────────────────
  const addRow = () => setRows(r => [...r, EMPTY_ROW()]);
  const removeRow = (id) => setRows(r => r.filter(x => x.id !== id));

  const updateRow = (id, field, value) => {
    setRows(r => r.map(x => {
      if (x.id !== id) return x;
      const updated = { ...x, [field]: value };
      if (field === "type") {
        // Reset grade to first valid option for the new type
        updated.grade = GRADE_OPTIONS[value][0];
      }
      return updated;
    }));
  };

  // ── Calculate ───────────────────────────────────────────────────────────────
  const calculate = useCallback(async () => {
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const qualifications = rows.map(r => ({ qualification_type: r.type, grade: r.grade }));
      const { data } = await apiRequest("post", "/tools/gpa-converter", { qualifications });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not calculate GPA. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [rows]);

  // ── Reverse lookup (computed from form state, not API) ───────────────────────
  const { sumPoints, totalWeight } = (() => {
    let sp = 0, tw = 0;
    for (const r of rows) {
      const w = WEIGHTS[r.type] || 0;
      const gpa = (GRADE_GPA[r.type] || {})[r.grade];
      if (w > 0 && gpa !== undefined) { sp += gpa * w; tw += w; }
    }
    return { sumPoints: sp, totalWeight: tw };
  })();

  // Divisions where player is not yet Eligible
  const divisionsNeedingImprovement = result
    ? DIVISIONS.filter(d => result[d.key] !== "Eligible")
    : [];

  // ── Copy to clipboard ────────────────────────────────────────────────────────
  const copyResult = () => {
    if (!result) return;
    const lines = [
      "CourtBound UK GPA Calculator",
      `US GPA: ${result.us_gpa ?? "N/A"}`,
      `NCAA D1: ${result.d1_status}`,
      `NCAA D2: ${result.d2_status}`,
      `NCAA D3: ${result.d3_status}`,
      `NAIA: ${result.naia_status}`,
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Save to profile ────────────────────────────────────────────────────────
  // PATCHes gpa_equivalent onto the player's profile record.
  // Silently ignored if the user is not logged in (401) — no hard crash.
  const saveToProfile = async () => {
    if (!result?.us_gpa) return;
    setSavingGpa(true);
    setSaveError("");
    try {
      await apiRequest("put", "/profile", { gpa_equivalent: String(result.us_gpa.toFixed(2)) });
      setSavedGpa(true);
      setTimeout(() => setSavedGpa(false), 4000);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setSaveError("Log in to save your GPA to your profile.");
      } else {
        setSaveError("Could not save — please try again.");
      }
      setTimeout(() => setSaveError(""), 4000);
    } finally {
      setSavingGpa(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <div className="mb-6">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">Academic Tool</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-1"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
          UK GPA Calculator
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Convert your UK qualifications to a US GPA and check NCAA / NAIA eligibility
        </p>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 flex gap-3">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Add your qualifications below. <strong>A-Levels, BTECs, Scottish Highers, and IB Diploma</strong> all count toward your US GPA.
          Enter your IB Diploma as a total points score (e.g. 38).
        </p>
      </div>

      {/* GCSE note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <strong>GCSEs are not included in your US GPA.</strong> US university admissions offices
          evaluate GPA from your <em>post-16</em> qualifications only (A-Levels, IB, BTEC, Scottish Highers).
          Your GCSEs are still part of your overall academic story — coaches and admissions teams
          see them as evidence of your early academic foundation — but they carry no GPA weight.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* ── Left: Builder ───────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">

          {/* Qualification rows */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2"
              style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">1</span>
              Your Qualifications
            </h2>

            <div className="space-y-3" data-testid="qualification-rows">
              {rows.map((row, idx) => (
                <div key={row.id} className="flex items-center gap-2" data-testid={`qual-row-${idx}`}>

                  {/* Type */}
                  <div className="relative flex-1">
                    <select
                      data-testid={`qual-type-${idx}`}
                      value={row.type}
                      onChange={e => updateRow(row.id, "type", e.target.value)}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-7"
                    >
                      {Object.entries(TYPE_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>

                  {/* Grade */}
                  <div className="relative w-24">
                    <select
                      data-testid={`qual-grade-${idx}`}
                      value={row.grade}
                      onChange={e => updateRow(row.id, "grade", e.target.value)}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-7"
                    >
                      {GRADE_OPTIONS[row.type].map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>

                  {/* Remove */}
                  <button
                    data-testid={`remove-row-${idx}`}
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-red-50"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <button
              data-testid="add-qualification-btn"
              onClick={addRow}
              className="mt-4 w-full border-2 border-dashed border-slate-300 text-slate-500 rounded-lg py-2.5 text-sm font-semibold hover:border-orange-400 hover:text-orange-500 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Qualification
            </button>
          </div>

          {/* Grade reference */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="font-bold text-slate-900 mb-3 text-xs uppercase tracking-widest text-slate-500">
              Grade → GPA Reference
            </h2>
            <div className="space-y-2 text-xs">
              <div>
                <span className="font-bold text-slate-700">A-Level: </span>
                <span className="text-slate-500">A*/A = 4.0 · B = 3.3 · C = 2.7 · D = 2.0 · E = 1.0</span>
              </div>
              <div>
                <span className="font-bold text-slate-700">BTEC: </span>
                <span className="text-slate-500">D* = 4.0 · D = 3.7 · M = 3.0 · P = 2.0</span>
              </div>
              <div>
                <span className="font-bold text-slate-700">Scottish Higher: </span>
                <span className="text-slate-500">A = 4.0 · B = 3.3 · C = 2.7 · D = 2.0</span>
              </div>
              <div>
                <span className="font-bold text-slate-700">BTEC Diploma: </span>
                <span className="text-slate-500">counts as 2 A-Level equivalents</span>
              </div>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </p>
          )}

          <button
            data-testid="calculate-gpa-btn"
            onClick={calculate}
            disabled={loading}
            className="w-full bg-orange-500 text-white font-bold uppercase tracking-wider rounded-xl py-3.5 text-sm hover:bg-orange-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating...</>
              : <><GraduationCap className="w-4 h-4" /> Calculate GPA</>
            }
          </button>
        </div>

        {/* ── Right: Results ──────────────────────────────────────────────── */}
        <div className="lg:col-span-3 space-y-4">

          {loading ? (
            <div className="bg-white border border-slate-200 rounded-xl p-16 flex flex-col items-center justify-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent" />
              <p className="text-slate-400 text-sm">Calculating your US GPA...</p>
            </div>

          ) : result ? (
            <>
              {/* GPA Result Card */}
              <div data-testid="gpa-result-card" className="bg-white border border-slate-200 rounded-xl p-6">

                {/* Header row */}
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-900"
                        style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Your GPA Result
                      </h2>
                      <p className="text-xs text-slate-400 mt-0.5">Based on {rows.length} qualification{rows.length !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="copy-result-btn"
                      onClick={copyResult}
                      className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-orange-600 border border-slate-200 hover:border-orange-300 rounded-lg px-3 py-1.5 transition-all"
                      title="Copy results"
                    >
                      {copied ? <><Check className="w-3.5 h-3.5 text-green-600" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                    </button>
                    {result?.us_gpa !== null && result?.us_gpa !== undefined && (
                      <button
                        data-testid="save-gpa-to-profile-btn"
                        onClick={saveToProfile}
                        disabled={savingGpa || savedGpa}
                        className={`flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 transition-all border ${
                          savedGpa
                            ? "bg-green-50 border-green-300 text-green-700"
                            : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                        } disabled:opacity-60`}
                        title="Save this GPA to your CourtBound profile"
                      >
                        {savedGpa
                          ? <><Check className="w-3.5 h-3.5 text-green-600" /> Saved</>
                          : savingGpa
                          ? <>Saving…</>
                          : <><Save className="w-3.5 h-3.5" /> Save to profile</>
                        }
                      </button>
                    )}
                  </div>
                </div>

                {/* GPA Number */}
                <div className="text-center mb-6">
                  {result.us_gpa !== null ? (
                    <>
                      <div data-testid="gpa-value" className="text-7xl font-black text-slate-900"
                        style={{ fontFamily: "Barlow Condensed, sans-serif", lineHeight: 1 }}>
                        {result.us_gpa.toFixed(2)}
                      </div>
                      <div className="text-sm text-slate-500 mt-2 font-semibold uppercase tracking-widest">US GPA (4.0 Scale)</div>
                    </>
                  ) : (
                    <div className="text-slate-400 text-base">
                      No scoreable qualifications — GCSEs are excluded from GPA calculation.
                    </div>
                  )}
                </div>

                {/* Division status grid */}
                <div className="grid grid-cols-2 gap-3" data-testid="division-status-grid">
                  {DIVISIONS.map(div => {
                    const status = result[div.key];
                    const c = statusColors(status);
                    return (
                      <div
                        key={div.key}
                        data-testid={`division-${div.key}`}
                        className={`${c.bg} ${c.border} border rounded-xl p-3.5 flex items-center gap-3`}
                      >
                        <div className={`${c.badge} rounded-full p-1.5 text-white flex-shrink-0`}>
                          {statusIcon(status)}
                        </div>
                        <div>
                          <div className="text-xs font-black uppercase tracking-wider text-slate-700"
                            style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                            {div.label}
                          </div>
                          <div className={`text-sm font-bold ${c.text}`}>{status}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* GCSE note */}
                {result.gcse_note && (
                  <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 flex gap-2">
                    <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-500">{result.gcse_note}</p>
                  </div>
                )}

                {/* Merit-aid tier */}
                {result.merit_aid_tier && result.us_gpa !== null && (
                  <div
                    data-testid="merit-aid-tier-card"
                    className={`mt-4 rounded-xl border p-4 flex items-start gap-3 ${
                      result.merit_aid_tier === "Strong merit-aid candidate"
                        ? "bg-emerald-50 border-emerald-200"
                        : result.merit_aid_tier === "Possible partial merit aid"
                        ? "bg-blue-50 border-blue-200"
                        : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <GraduationCap className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      result.merit_aid_tier === "Strong merit-aid candidate" ? "text-emerald-600"
                      : result.merit_aid_tier === "Possible partial merit aid" ? "text-blue-600"
                      : "text-slate-400"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-black uppercase tracking-wider text-slate-500">Academic Merit Aid</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          result.merit_aid_tier === "Strong merit-aid candidate"
                            ? "bg-emerald-100 text-emerald-700"
                            : result.merit_aid_tier === "Possible partial merit aid"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-100 text-slate-500"
                        }`}>
                          {result.merit_aid_tier}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed" data-testid="merit-aid-disclaimer">
                        {result.merit_aid_tier_disclaimer}
                      </p>
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-orange-700">
                    <strong>Disclaimer: </strong>
                    This is an estimate. NCAA eligibility is determined by the NCAA Eligibility Center.
                    Consult your recruiter or coach for official guidance.
                  </p>
                </div>

                {/* Save feedback */}
                {(saveError) && (
                  <div data-testid="gpa-save-error" className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    {saveError}
                  </div>
                )}
              </div>

              {/* Reverse Lookup Panel */}
              {divisionsNeedingImprovement.length > 0 && totalWeight > 0 && (
                <div data-testid="reverse-lookup-panel" className="bg-white border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-900"
                        style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Improve Your GPA
                      </h2>
                      <p className="text-xs text-slate-400 mt-0.5">Minimum next qualification to reach Eligible status</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {divisionsNeedingImprovement.map(div => {
                      const lookup = computeReverseLookup(sumPoints, totalWeight, div.threshold);
                      const hasAnyOption = Object.values(lookup).some(v => v !== null);

                      return (
                        <div key={div.key} data-testid={`reverse-lookup-${div.key}`}>
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-xs font-black uppercase tracking-wider text-slate-700"
                              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                              {div.label}
                            </span>
                            <span className="text-xs text-slate-400">→ needs {div.threshold.toFixed(1)}+ GPA</span>
                          </div>

                          {!hasAnyOption ? (
                            <p className="text-xs text-slate-400 italic">
                              Not achievable by adding one more qualification alone.
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {SCOREABLE_TYPES.map(qType => {
                                const minGrade = lookup[qType];
                                return (
                                  <div
                                    key={qType}
                                    data-testid={`reverse-${div.key}-${qType}`}
                                    className={`flex items-center justify-between rounded-lg px-3 py-2.5 border text-xs ${
                                      minGrade
                                        ? "bg-green-50 border-green-200"
                                        : "bg-slate-50 border-slate-200 opacity-60"
                                    }`}
                                  >
                                    <span className="font-semibold text-slate-700">{TYPE_LABELS[qType].replace(" (excluded)", "")}</span>
                                    {minGrade ? (
                                      <span className="font-black text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                        {minGrade === "Any grade" ? "Any" : `≥ ${minGrade}`}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 italic">Not possible</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-slate-400 mt-5 italic">
                    These projections assume adding exactly one more qualification to your current list.
                  </p>
                </div>
              )}
            </>

          ) : (
            /* Empty state */
            <div className="bg-white border border-slate-200 rounded-xl p-14 text-center">
              <GraduationCap className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <h3 className="font-bold text-slate-600 text-lg mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
                Ready to Calculate?
              </h3>
              <p className="text-slate-400 text-sm max-w-xs mx-auto leading-relaxed">
                Select your qualification types and grades on the left, then click{" "}
                <strong>Calculate GPA</strong> to see your US GPA and eligibility status.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
